import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { Session } from '@supabase/supabase-js';
import { setAccessToken, userAPI } from '../utils/api';
import Loading from '../components/Loading';

interface AuthContextType {
  user: any | null;
  session: Session | null;
  tokens: { accessToken: string | undefined } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  reloadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await userAPI.getProfile();
      if (response && response.user) {
        setUser(response.user);
      } else {
        throw new Error('Profile not found');
      }
    } catch (error) {
      console.warn('Error fetching user profile, using session fallback:', error);
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        setUser({
          id: authUser.id,
          email: authUser.email,
          username: authUser.user_metadata?.username || authUser.email?.split('@')[0],
          displayName: authUser.user_metadata?.displayName || authUser.email?.split('@')[0],
          role: 'user',
          createdAt: authUser.created_at,
          updatedAt: authUser.updated_at,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const isElectron = Boolean(window.electronAPI);

    // Get initial session with CORS error handling for Electron
    const initSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('[AuthContext] Session error:', error);
          setIsLoading(false);
          return;
        }
        setSession(session);
        setAccessToken(session?.access_token ?? null);
        if (session?.user) {
          fetchUserProfile();
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.warn('[AuthContext] Failed to get session (CORS issue in Electron?):', err);
        // In Electron, if we can't reach Supabase, just continue without auth
        if (isElectron) {
          console.log('[AuthContext] Electron mode: continuing without Supabase session check');
        }
        setIsLoading(false);
      }
    };

    initSession();

    // Listen for changes with CORS error handling
    let subscription: { unsubscribe: () => void } | null = null;
    try {
      const {
        data: { subscription: sub },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
        setAccessToken(session?.access_token ?? null);
        if (session?.user) {
          fetchUserProfile();
        } else {
          setUser(null);
          setIsLoading(false);
        }
      });
      subscription = sub;
    } catch (err) {
      console.warn('[AuthContext] Failed to setup auth state listener:', err);
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [fetchUserProfile]);

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setAccessToken(null);
  };

  const isAuthenticated = !!session;
  const tokens = session ? { accessToken: session.access_token } : null;
  const reloadUser = fetchUserProfile;

  return (
    <AuthContext.Provider value={{ user, session, tokens, isAuthenticated, isLoading, logout, reloadUser }}>
      {!isLoading ? children : <Loading message="Initializing app..." />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};